import type { ApolloError } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { useQuery, useToastMutation } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import type { WithTypeName } from 'src/types';
import * as Schema from 'src/types/graphql';

import {
  updateNextRoleRightToWork,
  updateNextRoleRightToWorkFailure,
  updateNextRoleRightToWorkSuccess,
} from '../actionCreators';

import translations from './.vocab';
import type { DisplayedRightToWork, RightToWork } from './types';

export const useRightsToWork = () => {
  const { zone, language, isUnifiedSite } = useAppConfig();

  const { data, loading, error } = useQuery(Schema.GetRightsToWorkDocument, {
    variables: {
      zone,
      languageCode: language,
      isUnifiedSite,
    },
  });

  const rightsToWork = loading || error ? [] : data?.viewer?.rightsToWork || [];
  const [displayedRightsToWork, setDisplayedRightsToWork] =
    useState<DisplayedRightToWork[]>(rightsToWork);

  return {
    rightsToWork,
    displayedRightsToWork,
    setDisplayedRightsToWork,
    loading,
    error,
  };
};

const key = 'next-role-right-to-work';

export const useUpdateRightsToWork = () => {
  const dispatch = useDispatch();
  const { t } = useTranslations(translations);

  const onMutate = () => {
    dispatch(updateNextRoleRightToWork());
  };
  const onCompleted = () => {
    dispatch(updateNextRoleRightToWorkSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(updateNextRoleRightToWorkFailure(error));
  };

  return useToastMutation(Schema.UpdateRightsToWorkDocument, {
    key,
    errorActionRouteParameters: {
      to: 'right-to-work',
      mode: 'EDIT',
    },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Right to work updated'),
  });
};

/** Returns a mock response for use with the apollo MockedProvider */
export const mockGetRightsToWorkRequest = (
  rightsToWork: Array<WithTypeName<RightToWork>>,
) => ({
  request: {
    query: Schema.GetRightsToWorkDocument,
    variables: { zone: 'anz-1', languageCode: 'en', isUnifiedSite: true },
  },
  result: {
    data: {
      viewer: {
        _id: '1234',
        rightsToWork,
      },
    },
  },
});
